import { SwapRouterNativeAssets } from 'state/routing/types'
import type { RouterBuildRequest, RouterBuildResponse, RouterErrorResponse, RouterQuoteResponse } from './types'

/**
 * Router base URL. Fail-CLOSED: an unset, malformed, or unsafe URL resolves to '' and the fetch entry
 * points below reject with a config error instead of pointing live swaps somewhere wrong.
 *   - production: must be a well-formed https: URL with no embedded credentials and no fragment;
 *   - development: same, except plain http: is allowed ONLY for localhost / 127.0.0.1.
 * There is no silent localhost default in production.
 */
export function resolveRouterBaseUrl(
  env: { REACT_APP_KIBA_ROUTER_URL?: string; NODE_ENV?: string } = process.env,
): string {
  const raw = env.REACT_APP_KIBA_ROUTER_URL?.trim()
  const isProd = env.NODE_ENV === 'production'
  if (!raw) return isProd ? '' : 'http://localhost:8787'

  let url: URL
  try {
    url = new URL(raw)
  } catch {
    return '' // malformed → fail closed
  }
  if (url.username || url.password) return '' // no embedded credentials
  if (url.hash) return '' // no fragment
  // No query string: endpoints are built by string concatenation (`${base}/v1/quote`), so a base like
  // `https://host/router?tenant=x` would produce the invalid URL `https://host/router?tenant=x/v1/quote`.
  // A path prefix (e.g. `https://host/router`) is fine and concatenates correctly.
  if (url.search) return ''

  const isLoopback = url.hostname === 'localhost' || url.hostname === '127.0.0.1'
  const httpsOk = url.protocol === 'https:'
  const devHttpOk = !isProd && url.protocol === 'http:' && isLoopback
  if (!httpsOk && !devHttpOk) return ''

  return raw.replace(/\/$/, '')
}

export const ROUTER_BASE_URL = resolveRouterBaseUrl()

function configError(): RouterErrorResponse {
  return { error: { code: 'RPC_UNAVAILABLE', message: 'router URL not configured (REACT_APP_KIBA_ROUTER_URL)' } }
}

/** Zero-address the router uses to denote the chain's native currency. */
const NATIVE_ROUTER_ADDRESS = '0x0000000000000000000000000000000000000000'
const NATIVE_SENTINELS = new Set<string>(Object.values(SwapRouterNativeAssets).map((s) => s.toLowerCase()))

/**
 * The web app represents native currency as a sentinel string (`SwapRouterNativeAssets.ETH` === `'ETH'`),
 * but the router validates token fields as EVM addresses and expects the zero-address for native.
 * Normalize any native sentinel to the zero-address before sending; pass real addresses through untouched.
 */
export function toRouterTokenAddress(address: string): string {
  return NATIVE_SENTINELS.has(address.toLowerCase()) ? NATIVE_ROUTER_ADDRESS : address
}

export function isRouterError(r: unknown): r is RouterErrorResponse {
  return !!r && typeof r === 'object' && 'error' in r && typeof (r as RouterErrorResponse).error?.code === 'string'
}

function networkError(message: string): RouterErrorResponse {
  return { error: { code: 'RPC_UNAVAILABLE', message } }
}

function timeoutError(message: string): RouterErrorResponse {
  return { error: { code: 'RPC_UNAVAILABLE', message } }
}

// Client abort budgets MUST exceed the router's maximum sequential processing time. Router config caps
// quote stages at 5s and build simulation at 10s specifically to preserve this static contract:
//   - /v1/quote: decimals + discovery + block number + quoter = at most ~20s.
//   - /v1/build: fresh block + re-quote + simulation = at most ~20s.
// 30s leaves ~10s for queueing and network/HTTP overhead. Keep the config maxima and these constants
// synchronized; config tests reject a server setting that would invalidate this browser budget.
const QUOTE_TIMEOUT_MS = 30_000
const BUILD_TIMEOUT_MS = 30_000

/** True when `e` is the DOMException/Error thrown by an aborted fetch (browser and node-fetch/undici agree on the name). */
function isAbortError(e: unknown): boolean {
  return !!e && typeof e === 'object' && (e as { name?: string }).name === 'AbortError'
}

export async function fetchRouterQuote(input: {
  tokenIn: string
  tokenOut: string
  amount: string
  sender?: string
}): Promise<RouterQuoteResponse | RouterErrorResponse> {
  if (!ROUTER_BASE_URL) return configError()
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), QUOTE_TIMEOUT_MS)
  try {
    const res = await fetch(`${ROUTER_BASE_URL}/v1/quote`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        chainId: 4663,
        tokenIn: toRouterTokenAddress(input.tokenIn),
        tokenOut: toRouterTokenAddress(input.tokenOut),
        amount: input.amount,
        tradeType: 'EXACT_INPUT',
        ...(input.sender ? { sender: input.sender } : {}),
      }),
      signal: controller.signal,
    })
    const body = await res.json().catch(() => null)
    if (!res.ok) return isRouterError(body) ? body : networkError(`router quote HTTP ${res.status}`)
    return body as RouterQuoteResponse
  } catch (e) {
    if (isAbortError(e)) return timeoutError('router request timed out')
    return networkError((e as Error)?.message ?? 'router unreachable')
  } finally {
    clearTimeout(timer)
  }
}

export async function fetchRouterBuild(req: RouterBuildRequest): Promise<RouterBuildResponse | RouterErrorResponse> {
  if (!ROUTER_BASE_URL) return configError()
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), BUILD_TIMEOUT_MS)
  try {
    const res = await fetch(`${ROUTER_BASE_URL}/v1/build`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(req),
      signal: controller.signal,
    })
    const body = await res.json().catch(() => null)
    if (!res.ok) return isRouterError(body) ? body : networkError(`router build HTTP ${res.status}`)
    return body as RouterBuildResponse
  } catch (e) {
    if (isAbortError(e)) return timeoutError('router request timed out')
    return networkError((e as Error)?.message ?? 'router unreachable')
  } finally {
    clearTimeout(timer)
  }
}
